/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { theme } from '_constants/theme';

const { colors, devices, fontSizes, spaces } = theme;

const StyledBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
`;

const StyledBannerContainer = styled(
  forwardRef(({ reverse, ...props }, ref) => <div ref={ref} {...props} />)
)`
  display: flex;
  justify-content: space-between;
  max-width: ${spaces.large24X * 10 + 80}px;
  width: 100%;
  padding: ${spaces.regular}px;

  ${({ reverse }) =>
    reverse &&
    css`
      flex-direction: row-reverse;
    `}

  @media ${devices.tablet} {
    flex-direction: column;
  }
`;

const StyledImageContainer = styled(
  forwardRef(({ desktop, mobile, ...props }, ref) => (
    <div ref={ref} {...props} />
  ))
)`
  display: flex;
  justify-content: center;
  align-items: center;

  min-height: 450px;
  width: 50%;

  img {
    max-height: 450px;
  }

  ${({ mobile }) =>
    mobile &&
    css`
      display: none;
    `}

  @media ${devices.tablet} {
    width: 100%;
    min-height: 200px;
    margin-bottom: ${spaces.regular}px;

    img {
      width: 100%;
    }

    ${({ desktop }) =>
      desktop &&
      css`
        display: none;
      `}

    ${({ mobile }) =>
      mobile &&
      css`
        display: flex;
      `}
  }
`;

const StyledBannerBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  width: 50%;

  @media ${devices.tablet} {
    align-items: center;
    width: 100%;
  }
`;

const StyledTitle = styled.div`
  margin-bottom: ${spaces.largeX + 2}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.large11X}px;
  line-height: 130%;
  color: ${colors.gray[800]};

  @media ${devices.tablet} {
    margin-bottom: ${spaces.regular}px;
    font-size: ${fontSizes.large2X}px;
  }
`;

const StyledDescription = styled.div`
  margin-bottom: ${spaces.largeX + 2}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.medium}px;
  line-height: 150%;
  text-align: left;
  color: ${colors.gray[700]};

  @media ${devices.tablet} {
    margin-bottom: ${spaces.regular}px;
    font-size: ${fontSizes.regular}px;
  }
`;

const BaseInfoSection = ({ reverse, imgSrc, imgAlt, title, description }) => {
  return (
    <StyledBanner>
      <StyledBannerContainer reverse={reverse}>
        <StyledBannerBody>
          <StyledTitle>{title}</StyledTitle>
          <StyledDescription>{description}</StyledDescription>
          <StyledImageContainer mobile>
            <picture>
              <source srcSet={imgSrc} type="image/svg+xml" />
              <img src={imgSrc} alt={imgAlt} />
            </picture>
          </StyledImageContainer>
        </StyledBannerBody>
        <StyledImageContainer desktop>
          <picture>
            <source srcSet={imgSrc} type="image/svg+xml" />
            <img src={imgSrc} alt={imgAlt} />
          </picture>
        </StyledImageContainer>
      </StyledBannerContainer>
    </StyledBanner>
  );
};

BaseInfoSection.propTypes = {
  reverse: PropTypes.bool,
  imgSrc: PropTypes.string,
  imgAlt: PropTypes.string,
  title: PropTypes.node,
  description: PropTypes.node,
};

export default BaseInfoSection;
